/**
 * ReparatieTab — exemplaren op reparatie + schademeldingen uit einde-draaidag-rapporten.
 * Vervangt de reparatie-interface in Airtable (functioneel, niet 1-op-1; 26 sep 2026).
 *
 * Bovenaan: meldingen (voorstel — er gaat niets vanzelf op reparatie). Daaronder de lijst
 * met verantwoordelijke, reparatiestatus, opmerkingen, sinds-datum en 'Weer beschikbaar'.
 * Elke wijziging krijgt undo (utils/ongedaan). Schrijven: engine → PG + Airtable; op test
 * staat Airtable-schrijven uit en dat staat er dan rustig bij.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, Autocomplete, MenuItem, Select, FormControl, CircularProgress,
  Dialog, DialogContent, Tooltip,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';

const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  error: '#a83836', amber: '#f59e0b',
  font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif',
};

const API = `${BACKEND_URL}/Api/materiaal-reparatie`;
const STATUSSEN = ['Reparatie, niet bruikbaar', 'Reparatie, wel bruikbaar'];
const REPARATIESTATUSSEN = [
  '(Nog) geen actie ondernomen', 'Reparatie intern nog inplannen', 'Reparatie intern ingepland',
  'Reserve onderdelen besteld', 'Bij reparateur',
];

const Icon = ({ name, size = 18, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, lineHeight: 1, display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', ...sx }}>{name}</span>
);
const Label = ({ children }) => (
  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 700, color: T.outlineVariant, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>{children}</Typography>
);
const selectSx = { borderRadius: '0.5rem', fontFamily: T.fontBody, fontSize: '0.8rem', bgcolor: T.surface };
const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '0.5rem', fontFamily: T.fontBody, fontSize: '0.8rem', bgcolor: T.surface } };
const knopSx = { display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.75rem', color: T.secondary, border: `1px solid ${T.surfaceHigh}`, bgcolor: T.surface, whiteSpace: 'nowrap', '&:hover': { bgcolor: T.surfaceLow } };

export const fmtDatum = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Tekst die bij 'Op reparatie zetten' vanuit een melding in de opmerkingen komt. */
export const opmerkingUitMelding = (melding) => {
  if (!melding) return '';
  const waar = melding.draaidagNaam || melding.projectNaam || 'draaidag';
  const wanneer = fmtDatum(melding.ingevuldOp);
  const wie = melding.ingevuldDoor ? `, ${melding.ingevuldDoor}` : '';
  const kop = `Schade gemeld bij einde draaidag ${waar}${wanneer ? ` (${wanneer}${wie})` : (wie ? ` (${wie.slice(2)})` : '')}`;
  return melding.opmerking ? `${kop}: ${melding.opmerking}` : kop;
};

/** Huidige waarden van een exemplaar in de vorm die de PATCH terug accepteert (voor undo). */
export const momentopname = (item) => ({
  status: item.status || '',
  reparatiestatus: item.reparatiestatus || '',
  opmerkingen: item.opmerkingen || '',
  assigneeIds: item.assigneeIds || [],
  sinds: item.sinds || null,
  rapportId: item.rapportId || null,
});

/**
 * Welke velden undo terugzet: alleen wat deze actie raakte. Status/reparatiestatus
 * bepalen ook 'sinds' en de rapportkoppeling (afgeleid in de engine), dus die gaan mee.
 */
export const undoVelden = (body) => {
  const keys = Object.keys(body || {}).filter((k) => ['status', 'reparatiestatus', 'opmerkingen', 'assigneeIds', 'rapportId'].includes(k));
  if (keys.includes('status') || keys.includes('reparatiestatus')) keys.push('sinds', 'rapportId');
  return [...new Set(keys)];
};
const kies = (obj, keys) => Object.fromEntries(keys.map((k) => [k, obj[k]]));

/** Een exemplaar uit de catalogus (materialen-lijst) → dezelfde vorm als een reparatie-item. */
export const uitCatalogus = (m) => {
  const f = m?.fields || {};
  return {
    id: m.id, naam: f.ID || f.TypeText || 'Exemplaar', status: f.Status || '', reparatiestatus: f.Reparatiestatus || '',
    opmerkingen: f['Reparatie Opmerkingen'] || '', assigneeIds: f['Reparatie Assignee'] || [],
    sinds: f.ReparatieSinds || null, rapportId: f.ReparatieRapportId || null,
  };
};

function Personenkiezer({ personen, waarde, onChange, placeholder = 'Verantwoordelijke…' }) {
  const gekozen = useMemo(() => (waarde || []).map((id) => personen.find((p) => p.id === id) || { id, naam: 'Onbekend' }), [personen, waarde]);
  return (
    <Autocomplete multiple size="small" options={personen} value={gekozen}
      getOptionLabel={(o) => o.naam} isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_, v) => onChange(v.map((p) => p.id))}
      renderInput={(params) => <TextField {...params} placeholder={gekozen.length ? '' : placeholder} sx={inputSx} />}
      ChipProps={{ size: 'small', sx: { fontFamily: T.fontBody, fontSize: '0.7rem', height: 22 } }}
      sx={{ minWidth: 180 }} />
  );
}

function OpmerkingVeld({ waarde, onOpslaan }) {
  const [tekst, setTekst] = useState(waarde || '');
  useEffect(() => { setTekst(waarde || ''); }, [waarde]);
  return (
    <TextField size="small" fullWidth multiline minRows={1} maxRows={6} placeholder="Opmerkingen over de reparatie…"
      value={tekst} onChange={(e) => setTekst(e.target.value)}
      onBlur={() => { if ((tekst || '') !== (waarde || '')) onOpslaan(tekst); }}
      sx={inputSx} />
  );
}

export default function ReparatieTab({ materials = [], onOpenExemplaar, onChanged }) {
  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(true);
  const [personen, setPersonen] = useState([]);
  const [dialoog, setDialoog] = useState(null); // { item, melding, form }
  const [bezig, setBezig] = useState(false);

  const laad = useCallback(async () => {
    try {
      const r = await axios.get(API);
      setData(r.data);
    } catch (err) {
      toast.error('Reparaties laden mislukt: ' + (err.response?.data?.detail || err.message));
    } finally { setLaden(false); }
  }, []);

  useEffect(() => { laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/Api/personen`).then((r) => {
      const recs = r.data?.records || [];
      setPersonen(recs.map((p) => ({ id: p.id, naam: p.fields?.['Volledige naam'] || p.fields?.Naam || '' }))
        .filter((p) => p.naam).sort((a, b) => a.naam.localeCompare(b.naam)));
    }).catch(() => { /* kiezer blijft leeg; tonen van namen komt uit de engine */ });
  }, []);

  const verwerkAntwoord = (res) => {
    if (res?.airtable === 'mislukt' && res?.melding) toast.error(res.melding);
  };

  const patch = useCallback(async (id, body) => {
    const r = await axios.patch(`${API}/${id}`, body);
    verwerkAntwoord(r.data);
    return r.data;
  }, []);

  /** Wijzigen met undo: eerst de oude waarden vastleggen, dan schrijven, dan herladen. */
  const wijzig = useCallback(async (item, body, tekst) => {
    const keys = undoVelden(body);
    const oud = kies(momentopname(item), keys);
    try {
      const res = await patch(item.id, body);
      await laad();
      onChanged && onChanged();
      // Undo zet alleen de geraakte velden terug, en alleen als die nog staan zoals wij ze
      // achterlieten (verwacht → anders 409 van de engine, geen stil overschrijven).
      const verwacht = res?.item ? kies(momentopname(res.item), keys) : undefined;
      biedOngedaanAan(tekst, async () => {
        try {
          await patch(item.id, { ...oud, ...(verwacht ? { verwacht } : {}) });
        } catch (e) {
          throw new Error(e.response?.data?.detail || e.message);
        } finally {
          await laad();
          onChanged && onChanged();
        }
      });
    } catch (err) {
      toast.error('Opslaan mislukt: ' + (err.response?.data?.detail || err.message));
    }
  }, [patch, laad, onChanged]);

  const handelMeldingAf = async (melding) => {
    try {
      await axios.post(`${API}/meldingen/${melding.id}/afhandeling`);
      await laad();
      biedOngedaanAan('Melding afgehandeld', async () => {
        await axios.delete(`${API}/meldingen/${melding.id}/afhandeling`);
        await laad();
      });
    } catch (err) {
      toast.error('Afhandelen mislukt: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openDialoog = (materiaal, melding = null) => {
    const item = materiaal.fields ? uitCatalogus(materiaal) : materiaal;
    setDialoog({
      item, melding,
      form: {
        status: STATUSSEN.includes(item.status) ? item.status : STATUSSEN[0],
        reparatiestatus: item.reparatiestatus || REPARATIESTATUSSEN[0],
        assigneeIds: item.assigneeIds || [],
        opmerkingen: [item.opmerkingen, opmerkingUitMelding(melding)].filter(Boolean).join('\n\n'),
      },
    });
  };

  const zetOpReparatie = async () => {
    if (!dialoog) return;
    const { item, melding, form } = dialoog;
    setBezig(true);
    await wijzig(item, { ...form, ...(melding ? { rapportId: melding.id } : {}) }, `${item.naam} op reparatie gezet`);
    setBezig(false);
    setDialoog(null);
  };

  const items = useMemo(() => data?.items || [], [data]);
  const meldingen = data?.meldingen || [];
  const opReparatieIds = useMemo(() => new Set(items.map((i) => i.id)), [items]);
  const kiesbaar = useMemo(() => materials
    .filter((m) => !opReparatieIds.has(m.id) && m.fields?.Status !== 'Verkocht/gearchiveerd')
    .map((m) => ({ id: m.id, label: m.fields?.ID || m.fields?.TypeText || m.id, m }))
    .sort((a, b) => a.label.localeCompare(b.label)), [materials, opReparatieIds]);

  if (laden) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} sx={{ color: T.tertiary }} /></Box>;
  }

  return (
    <Box>
      {data && data.airtableSchrijven === false && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
          <Icon name="info" size={16} sx={{ color: T.outline }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline }}>
            Schrijven naar Airtable staat uit op test — wijzigingen blijven in Toolframe.
          </Typography>
        </Box>
      )}
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline, mb: 2 }}>
        Wijzig reparaties hier, niet in Airtable: Toolframe is leidend voor status, reparatiestatus, opmerkingen, verantwoordelijke en opslaglocatie.
      </Typography>

      {/* Meldingen uit einde-draaidag-rapporten */}
      {meldingen.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '0.95rem', fontWeight: 800, color: T.text }}>
            Schademeldingen ({meldingen.length})
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, mb: 1.5 }}>
            Uit einde-draaidag-rapporten. Een voorstel: kies zelf welk materiaal op reparatie gaat.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {meldingen.map((mel) => (
              <Box key={mel.id} sx={{ p: 1.75, borderRadius: '0.7rem', bgcolor: T.surface, border: `1px solid ${T.surfaceHigh}`, borderLeft: `3px solid ${T.amber}` }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 700, color: T.text }}>
                      {mel.draaidagNaam || mel.projectNaam || 'Draaidag'}
                    </Typography>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outline }}>
                      {[mel.projectNummer && mel.projectNaam && mel.draaidagNaam ? `${mel.projectNummer} ${mel.projectNaam}` : null,
                        fmtDatum(mel.ingevuldOp), mel.ingevuldDoor].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  <Tooltip title="Klaar met deze melding (ongedaan te maken)" arrow>
                    <Box onClick={() => handelMeldingAf(mel)} sx={knopSx}><Icon name="done" size={16} /> Afhandelen</Box>
                  </Tooltip>
                </Box>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.secondary, mt: 1, whiteSpace: 'pre-wrap' }}>
                  {mel.opmerking || 'Schade aangevinkt, zonder toelichting.'}
                </Typography>
                {mel.gekoppeld?.length > 0 && (
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary, mt: 1 }}>
                    Op reparatie gezet: {mel.gekoppeld.map((g) => g.naam).join(', ')}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 1.25 }}>
                  {mel.kandidaten?.length > 0 && (
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outline, mr: 0.5 }}>Gereserveerd:</Typography>
                  )}
                  {(mel.kandidaten || []).map((k) => (
                    <Tooltip key={k.id} title={k.opReparatie ? 'Staat al op reparatie' : 'Op reparatie zetten'} arrow>
                      <Box onClick={() => {
                        if (k.opReparatie) return;
                        const mat = materials.find((m) => m.id === k.id);
                        openDialoog(mat || { id: k.id, naam: k.naam, status: '', reparatiestatus: '', opmerkingen: '', assigneeIds: [] }, mel);
                      }}
                        sx={{ ...knopSx, py: 0.3, px: 1, fontSize: '0.72rem', cursor: k.opReparatie ? 'default' : 'pointer', color: k.opReparatie ? T.outlineVariant : T.secondary }}>
                        {k.opReparatie && <Icon name="build" size={14} />}{k.naam}
                      </Box>
                    </Tooltip>
                  ))}
                  <Autocomplete size="small" options={kiesbaar} value={null} blurOnSelect clearOnBlur
                    getOptionLabel={(o) => o.label} isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(_, v) => { if (v) openDialoog(v.m, mel); }}
                    renderInput={(params) => <TextField {...params} placeholder={mel.kandidaten?.length ? 'Ander materiaal…' : 'Welk materiaal?'} sx={inputSx} />}
                    sx={{ width: 220 }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {data?.meldingenFout && (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.error, mb: 2 }}>
          Schademeldingen konden niet worden geladen.
        </Typography>
      )}

      {/* Op reparatie */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: T.font, fontSize: '0.95rem', fontWeight: 800, color: T.text, flex: 1 }}>
          Op reparatie ({items.length})
        </Typography>
        <Autocomplete size="small" options={kiesbaar} value={null} blurOnSelect clearOnBlur
          getOptionLabel={(o) => o.label} isOptionEqualToValue={(o, v) => o.id === v.id}
          onChange={(_, v) => { if (v) openDialoog(v.m); }}
          renderInput={(params) => <TextField {...params} placeholder="Materiaal op reparatie zetten…" sx={inputSx} />}
          sx={{ width: { xs: '100%', sm: 280 } }} />
      </Box>

      {items.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Icon name="task_alt" size={40} sx={{ color: T.outlineVariant }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.outline, mt: 1 }}>Niets op reparatie.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((it) => {
            const nietBruikbaar = it.status === 'Reparatie, niet bruikbaar' || it.status === 'Reparatie';
            return (
              <Box key={it.id} sx={{ p: 1.75, borderRadius: '0.7rem', bgcolor: T.surface, border: `1px solid ${T.surfaceHigh}` }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(180px,1.3fr) 190px 210px minmax(180px,1fr) auto' }, gap: 1.5, alignItems: 'center' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Box onClick={() => onOpenExemplaar && onOpenExemplaar(it.id)} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', maxWidth: '100%', '&:hover .naam': { textDecoration: 'underline' } }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: nietBruikbaar ? T.error : T.amber }} />
                      <Typography className="naam" sx={{ fontFamily: T.fontBody, fontSize: '0.86rem', fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.naam}</Typography>
                    </Box>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outline, pl: 2 }}>
                      {[it.sinds ? `sinds ${fmtDatum(it.sinds)}` : null, it.locatie, it.inAirtable ? null : 'alleen in Toolframe'].filter(Boolean).join(' · ') || ' '}
                    </Typography>
                  </Box>
                  <FormControl size="small" fullWidth>
                    <Select value={STATUSSEN.includes(it.status) || it.status === 'Reparatie' ? it.status : ''} displayEmpty
                      onChange={(e) => wijzig(it, { status: e.target.value }, 'Status bijgewerkt')} sx={selectSx}>
                      {it.status === 'Reparatie' && <MenuItem value="Reparatie" disabled sx={{ fontSize: '0.8rem' }}>Reparatie (oud)</MenuItem>}
                      {!STATUSSEN.includes(it.status) && it.status !== 'Reparatie' && <MenuItem value="" disabled sx={{ fontSize: '0.8rem' }}><em>{it.status || 'Geen status'}</em></MenuItem>}
                      {STATUSSEN.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <Select value={it.reparatiestatus || ''} displayEmpty
                      onChange={(e) => wijzig(it, { reparatiestatus: e.target.value }, 'Reparatiestatus bijgewerkt')} sx={selectSx}>
                      <MenuItem value="" sx={{ fontSize: '0.8rem' }}><em>Geen reparatiestatus</em></MenuItem>
                      {REPARATIESTATUSSEN.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Personenkiezer personen={personen.length ? personen : it.assignees} waarde={it.assigneeIds}
                    onChange={(ids) => wijzig(it, { assigneeIds: ids }, 'Verantwoordelijke bijgewerkt')} />
                  <Tooltip title="Status terug naar beschikbaar (ongedaan te maken)" arrow>
                    <Box onClick={() => wijzig(it, { status: '', reparatiestatus: '' }, `${it.naam} weer beschikbaar`)} sx={knopSx}>
                      <Icon name="check_circle" size={16} /> Weer beschikbaar
                    </Box>
                  </Tooltip>
                </Box>
                <Box sx={{ mt: 1.25 }}>
                  <OpmerkingVeld waarde={it.opmerkingen} onOpslaan={(t) => wijzig(it, { opmerkingen: t }, 'Opmerking opgeslagen')} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Op reparatie zetten */}
      <Dialog open={!!dialoog} onClose={() => !bezig && setDialoog(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '1rem' } }}>
        {dialoog && (
          <>
            <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: '1.05rem', fontWeight: 800, color: T.text }}>Op reparatie zetten</Typography>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.secondary }}>{dialoog.item.naam}</Typography>
            </Box>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
              <Box>
                <Label>Status</Label>
                <FormControl size="small" fullWidth>
                  <Select value={dialoog.form.status} onChange={(e) => setDialoog((d) => ({ ...d, form: { ...d.form, status: e.target.value } }))} sx={selectSx}>
                    {STATUSSEN.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <Label>Reparatiestatus</Label>
                <FormControl size="small" fullWidth>
                  <Select value={dialoog.form.reparatiestatus} onChange={(e) => setDialoog((d) => ({ ...d, form: { ...d.form, reparatiestatus: e.target.value } }))} sx={selectSx}>
                    {REPARATIESTATUSSEN.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <Label>Verantwoordelijke</Label>
                <Personenkiezer personen={personen} waarde={dialoog.form.assigneeIds}
                  onChange={(ids) => setDialoog((d) => ({ ...d, form: { ...d.form, assigneeIds: ids } }))} />
              </Box>
              <Box>
                <Label>Opmerkingen</Label>
                <TextField size="small" fullWidth multiline minRows={3} value={dialoog.form.opmerkingen}
                  onChange={(e) => setDialoog((d) => ({ ...d, form: { ...d.form, opmerkingen: e.target.value } }))} sx={inputSx} />
              </Box>
            </DialogContent>
            <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Box onClick={() => !bezig && setDialoog(null)} sx={{ px: 2, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.8rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>Annuleren</Box>
              <Box onClick={() => !bezig && zetOpReparatie()} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 2.5, py: 0.7, borderRadius: '0.5rem', cursor: bezig ? 'default' : 'pointer', bgcolor: T.primary, color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.8rem', opacity: bezig ? 0.7 : 1, '&:hover': { bgcolor: T.primaryDim } }}>
                {bezig && <CircularProgress size={14} sx={{ color: '#fff' }} />} Op reparatie zetten
              </Box>
            </Box>
          </>
        )}
      </Dialog>
    </Box>
  );
}
